import { ComponentProps, ForwardedRef, forwardRef } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import { TrackingLabel } from '../types/tracking'
import { WithClassName } from '../types/utils'
import { Colors } from '../style/colors'

const statesConfig = {
  true: {
    left: { width: 2.17571, height: 6.11829, transform: 'rotate(135 5.46143 10.56143) translate(5.46143 10.56143)' },
    right: { width: 2.17571, height: 11.9664, transform: 'rotate(45 10.56143 2.5) translate(10.56143 2)' },
  },
  false: {
    left: { width: 2.17571, height: 11.9664, transform: 'rotate(135 11 9.46143) translate(11 9.46143)' },
    right: { width: 2.17571, height: 11.9664, transform: 'rotate(45 9.46143 1) translate(9.46143 1)' },
  },
}

type ToggleProps = WithClassName &
  Omit<ComponentProps<'input'>, 'value'> & {
    value?: boolean
    trackingLabel: TrackingLabel
    variant?: 'small' | 'regular'
    fill?: string
  }

const ToggleIcon = ({ value, variant, fill }: Pick<ToggleProps, 'value' | 'variant' | 'fill'>) => {
  const config = statesConfig[`${!!value || false}`]
  const size = variant === 'small' ? 8 : 10

  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill={fill} xmlns="http://www.w3.org/2000/svg">
      <rect {...config.left} x={0} y={0} rx={1.08786} />
      <rect {...config.right} x={0} y={0} rx={1.08786} />
    </svg>
  )
}

const BaseToggle = ({ value, disabled, onClick, className, trackingLabel, variant = 'regular', ...props }: ToggleProps, ref: ForwardedRef<HTMLInputElement>) => {
  const isSmall = variant === 'small'
  const isActive = !!value

  const wrapperSize = isSmall ? { width: 32, height: 18, padding: 3 } : { width: 44, height: 24, padding: 4 }

  const circleSize = isSmall ? { width: 12, height: 12 } : { width: 16, height: 16 }

  const backgroundColor = disabled ? Colors.Silver : isActive ? Colors.Success : Colors.SlateGrey
  const iconFill = disabled ? Colors.Ash : backgroundColor

  const justifyContent = isActive ? 'flex-end' : 'flex-start'

  return (
    <span
      data-tracking={trackingLabel}
      onClick={disabled ? undefined : onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 9999,
        boxSizing: 'border-box',
        userSelect: 'none',
        transition: 'all 0.2s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor,
        justifyContent,
        width: wrapperSize.width,
        height: wrapperSize.height,
        padding: wrapperSize.padding,
      }}
      className={className}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 9999,
          backgroundColor: Colors.White,
          transition: 'transform 0.2s ease',
          width: circleSize.width,
          height: circleSize.height,
        }}
      >
        <ToggleIcon value={value} variant={variant} fill={iconFill} />
      </span>

      <input {...props} type="checkbox" defaultChecked={!!value} disabled={disabled} ref={ref} style={{ display: 'none' }} />
    </span>
  )
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(BaseToggle)

type ToggleInputProps<T extends FieldValues> = Omit<ToggleProps, 'value' | 'ref' | 'onChange'> &
  Omit<UseControllerProps<T>, 'defaultValue'> & {
    onChange?: (value: boolean) => void
    onClick?: () => void
  }

export const ToggleInput = <T extends FieldValues>({ name, trackingLabel, control, rules, shouldUnregister, onChange, onClick, className, ...props }: ToggleInputProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <Toggle
          trackingLabel={trackingLabel}
          {...props}
          value={field.value}
          onClick={() => {
            if (onClick) {
              onClick()
            } else {
              field.onChange(!field.value)
              onChange?.(!field.value)
            }
          }}
          className={className}
        />
      )}
    />
  )
}
